import { useMany, useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Rate, Table } from 'antd';
import type { PublicationListOut, ReviewOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { ReviewStatusTag } from '@/components/feedback/tags';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const ProfileReviewsTab = ({ profileId }: { profileId: number }) => {
  const t = useTranslate();
  const { tableProps, sorters, result } = useTable<ReviewOut>({
    resource: 'feedback.reviews',
    queryOptions: { enabled: profileId != null },
    filters: {
      permanent: [{ field: 'profile_id', operator: 'eq', value: profileId }],
    },
    // The reviews API has no updated_at sort.
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  const publicationIds = [
    ...new Set(
      (result?.data ?? []).map((r) => r.publication_id).filter(Boolean)
    ),
  ];
  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });
  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p: PublicationListOut) => [p.id, p])
  );

  return (
    <Card
      title={`${t('account.profiles.tabs.reviews')} (${result?.total ?? 0})`}
    >
      <Table<ReviewOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<ReviewOut>
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowIconLink resource="feedback.reviews" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<ReviewOut>
          dataIndex="publication_id"
          title={t('feedback.reviews.fields.publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
        />
        <Table.Column<ReviewOut>
          dataIndex="author_name"
          title={t('feedback.reviews.fields.author_name')}
        />
        <Table.Column<ReviewOut>
          dataIndex="rating"
          title={t('feedback.reviews.fields.rating')}
          sorter
          sortOrder={getSortOrder(sorters, 'rating')}
          render={(value: number) => <Rate allowHalf disabled value={value} />}
        />
        <Table.Column<ReviewOut>
          dataIndex="is_accepted"
          title={t('feedback.reviews.fields.status')}
          render={(_, record) => <ReviewStatusTag record={record} />}
        />
        <Table.Column<ReviewOut>
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string) => formatDateTime(value)}
        />
      </Table>
    </Card>
  );
};
